import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, TrendingUp, Award, Zap, CheckCircle, ArrowRight, Sparkles, Mail } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { branding } from '../config/branding';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';

const Landing = () => {
  const { currentTheme } = useTheme();
  const [email, setEmail] = useState('');

  const handleOptIn = (e) => {
    e.preventDefault();
    if (email) {
      toast({
        title: "You're In! 🎉",
        description: "Welcome to the VIP list. You'll be the first to know about new releases and exclusive drops.",
      });
      setEmail('');
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-30"
               style={{
                 background: `radial-gradient(circle at 30% 50%, ${currentTheme.primary}50, transparent 60%), 
                             radial-gradient(circle at 70% 60%, ${currentTheme.primary}30, transparent 50%)`
               }} />
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxIDAgNiAyLjY5IDYgNnMtMi42OSA2LTYgNi02LTIuNjktNi02IDIuNjktNiA2LTZ6TTI0IDQyYzMuMzEgMCA2IDIuNjkgNiA2cy0yLjY5IDYtNiA2LTYtMi42OS02LTYgMi42OS02IDYtNnoiIHN0cm9rZT0iIzMzMyIgc3Ryb2tlLXdpZHRoPSIyIi8+PC9nPjwvc3ZnPg==')] opacity-5" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 text-center py-20">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 backdrop-blur-sm border border-white/10 mb-8 animate-pulse">
            <Sparkles className="w-4 h-4" style={{ color: currentTheme.primary }} />
            <span className="text-sm text-white font-medium">Premium Hip-Hop Production</span>
          </div>

          <h1 className="text-6xl md:text-8xl lg:text-9xl font-black text-white mb-6 tracking-tight leading-none">
            YOUR NEXT
            <span className="block mt-2" style={{ color: currentTheme.primary }}>HIT STARTS</span>
            <span className="block mt-2">HERE</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            Industry-quality beats that transform artists into chart-toppers. 
            <span className="block mt-2">
              Join <span className="font-bold" style={{ color: currentTheme.primary }}>50+</span> artists who chose excellence.
            </span>
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link to="/home">
              <Button 
                size="lg" 
                className="h-16 px-12 text-xl font-black text-black hover:scale-105 transition-transform group"
                style={{ backgroundColor: currentTheme.primary }}
              >
                EXPLORE BEATS
                <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/home">
              <Button 
                size="lg" 
                variant="outline"
                className="h-16 px-12 text-xl font-black border-2 hover:scale-105 transition-transform hover:bg-white/5"
                style={{ borderColor: currentTheme.primary, color: currentTheme.primary }}
              >
                <Play className="mr-2 h-6 w-6 fill-current" />
                LISTEN NOW
              </Button>
            </Link>
          </div>

          {/* Social Proof */}
          <div className="grid grid-cols-3 gap-8 max-w-3xl mx-auto pt-8 border-t border-white/10">
            <div>
              <div className="text-4xl font-black text-white mb-2">{branding.stats.totalBeats}</div>
              <div className="text-gray-400 text-sm uppercase tracking-wider">Premium Beats</div>
            </div>
            <div>
              <div className="text-4xl font-black text-white mb-2">{branding.stats.artistsServed}</div>
              <div className="text-gray-400 text-sm uppercase tracking-wider">Artists Served & Growing</div>
            </div>
            <div>
              <div className="text-4xl font-black text-white mb-2">{branding.stats.yearsExperience}</div>
              <div className="text-gray-400 text-sm uppercase tracking-wider">Years Excellence</div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1 h-3 rounded-full" style={{ backgroundColor: currentTheme.primary }} />
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-black text-white mb-4 uppercase">
              Why We're Different
            </h2>
            <p className="text-xl text-gray-400">The production quality that separates amateurs from professionals</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Award,
                title: 'Industry Standard',
                description: 'Every beat is professionally mixed and mastered, ready for release. No amateur hour here.'
              },
              {
                icon: Zap,
                title: 'Instant Access',
                description: 'Download immediately after purchase. Start recording your hit in minutes, not days.'
              },
              {
                icon: TrendingUp,
                title: 'Chart-Ready Sound',
                description: 'Production quality used by platinum artists. Your competition is already using us.'
              }
            ].map((feature, idx) => (
              <div 
                key={idx}
                className="group relative p-8 rounded-2xl bg-black border border-white/10 hover:border-white/30 transition-all duration-300 hover:transform hover:scale-105"
              >
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"
                     style={{ background: `radial-gradient(circle at center, ${currentTheme.primary}10, transparent)` }} />
                <div className="relative">
                  <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform"
                       style={{ backgroundColor: `${currentTheme.primary}20` }}>
                    <feature.icon className="w-8 h-8" style={{ color: currentTheme.primary }} />
                  </div>
                  <h3 className="text-2xl font-black text-white mb-4 uppercase">{feature.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Opt-In Section */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20"
             style={{ background: `linear-gradient(135deg, ${currentTheme.primary}30, transparent)` }} />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 backdrop-blur-sm border border-white/10 mb-6">
            <Mail className="w-4 h-4" style={{ color: currentTheme.primary }} />
            <span className="text-sm text-white font-medium uppercase tracking-wider">VIP Access</span>
          </div>

          <h2 className="text-5xl md:text-7xl font-black text-white mb-6 uppercase leading-tight">
            STAY IN THE KNOW
          </h2>
          
          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-2xl mx-auto">
            Get exclusive access to new releases, limited drops, and production secrets before anyone else.
          </p>

          <form onSubmit={handleOptIn} className="max-w-2xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="flex-1 px-6 py-5 rounded-xl bg-white/5 backdrop-blur-sm border-2 border-white/10 text-white text-lg placeholder-gray-500 focus:outline-none focus:border-white/30 transition-all"
              />
              <Button
                type="submit"
                size="lg"
                className="h-auto px-10 py-5 text-xl font-black text-black hover:scale-105 transition-transform whitespace-nowrap"
                style={{ backgroundColor: currentTheme.primary }}
              >
                JOIN THE VIP LIST
              </Button>
            </div>
            <p className="text-gray-500 text-sm mt-4">
              No spam. Just heat. Unsubscribe anytime.
            </p>
          </form>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {[
              'First access to new beats',
              'Exclusive member discounts',
              'Production tips & secrets'
            ].map((benefit, idx) => (
              <div key={idx} className="flex items-center gap-3 justify-center md:justify-start">
                <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color: currentTheme.primary }} />
                <span className="text-gray-300">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-6xl font-black text-white mb-6 uppercase">
            Ready to Level Up?
          </h2>
          <p className="text-xl text-gray-400 mb-12">
            Your competition isn't waiting. Neither should you.
          </p>
          <Link to="/home">
            <Button 
              size="lg" 
              className="h-16 px-16 text-xl font-black text-black hover:scale-105 transition-transform"
              style={{ backgroundColor: currentTheme.primary }}
            >
              START BROWSING BEATS
              <ArrowRight className="ml-2 h-6 w-6" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Landing;